import React, { useState, useEffect } from 'react';
import { 
  HelpCircle, 
  X, 
  ChevronRight, 
  ChevronLeft,
  Play,
  CheckCircle2,
  Clock,
  Camera,
  MapPin,
  Route,
  Calendar,
  AlertTriangle,
  Home,
  Phone
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const HELP_SEEN_KEY = 'help_guide_seen';

const HelpGuide = () => {
  const [showBanner, setShowBanner] = useState(true); // Always show banner
  const [showGuide, setShowGuide] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const openGuide = () => {
    setShowGuide(true);
    setCurrentStep(0);
  };

  const steps = [
    {
      title: "Bienvenue !",
      icon: <Home className="h-12 w-12 text-blue-500" />,
      content: (
        <div className="space-y-4">
          <p className="text-lg text-gray-700">
            Cette application vous permet de <strong>gérer vos missions de ménage</strong> facilement depuis votre téléphone.
          </p>
          <div className="bg-blue-50 p-4 rounded-xl">
            <p className="text-blue-800">
              Suivez ce guide rapide pour comprendre comment utiliser l'application !
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Voir vos missions",
      icon: <Calendar className="h-12 w-12 text-purple-500" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Sur l'écran d'accueil, vous voyez <strong>les missions du jour</strong>.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <span className="text-2xl">📅</span>
              <p className="text-sm font-medium mt-1">Jour</p>
              <p className="text-xs text-gray-500">Mission par mission</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <span className="text-2xl">📆</span>
              <p className="text-sm font-medium mt-1">Semaine</p>
              <p className="text-xs text-gray-500">Vue globale</p>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg text-center">
              <span className="text-2xl">👀</span>
              <p className="text-sm font-medium mt-1">À venir</p>
              <p className="text-xs text-gray-500">4 semaines</p>
            </div>
            <div className="bg-emerald-50 p-3 rounded-lg text-center">
              <span className="text-2xl">✅</span>
              <p className="text-sm font-medium mt-1">Historique</p>
              <p className="text-xs text-gray-500">Missions terminées</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Accepter une mission",
      icon: <CheckCircle2 className="h-12 w-12 text-amber-500" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Quand on vous assigne une mission, elle apparaît en <strong className="text-amber-600">orange</strong>.
          </p>
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
            <p className="font-medium text-amber-800">Mission "À accepter"</p>
            <p className="text-sm text-amber-700 mt-1">
              Cliquez dessus puis appuyez sur <strong>"Accepter"</strong>
            </p>
          </div>
          <div className="flex items-center gap-2 text-emerald-600">
            <CheckCircle2 className="h-5 w-5" />
            <span>La mission passe en bleu : <strong>Acceptée</strong></span>
          </div>
        </div>
      )
    },
    {
      title: "Commencer le ménage",
      icon: <Play className="h-12 w-12 text-blue-500" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Quand vous arrivez au logement :
          </p>
          <div className="bg-blue-500 text-white p-4 rounded-xl text-center">
            <Play className="h-8 w-8 mx-auto mb-2" />
            <p className="font-bold text-lg">Cliquez sur "J'ARRIVE"</p>
            <p className="text-sm text-blue-100 mt-1">Le chrono démarre automatiquement</p>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="h-5 w-5" />
            <span>L'admin verra le temps que vous avez passé</span>
          </div>
        </div>
      )
    },
    {
      title: "Pendant le ménage",
      icon: <Camera className="h-12 w-12 text-green-500" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Pendant votre prestation, vous devez :
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">1</div>
              <div>
                <p className="font-medium">Cocher les tâches</p>
                <p className="text-sm text-gray-500">Au fur et à mesure que vous les faites</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">2</div>
              <div>
                <p className="font-medium">Prendre 5 photos minimum</p>
                <p className="text-sm text-gray-500">Pour prouver le travail effectué</p>
              </div>
            </div>
          </div>
          <div className="bg-red-50 p-3 rounded-lg flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">
              <strong>Important :</strong> Les 5 photos sont obligatoires pour terminer
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Terminer la mission",
      icon: <CheckCircle2 className="h-12 w-12 text-emerald-500" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Une fois le ménage terminé :
          </p>
          <div className="bg-emerald-500 text-white p-4 rounded-xl text-center">
            <CheckCircle2 className="h-8 w-8 mx-auto mb-2" />
            <p className="font-bold text-lg">Cliquez sur "J'AI TERMINÉ"</p>
            <p className="text-sm text-emerald-100 mt-1">Le chrono s'arrête</p>
          </div>
          <div className="bg-emerald-50 p-3 rounded-lg">
            <p className="text-emerald-800">
              La mission passe en <strong className="text-emerald-600">vert</strong> et apparaît dans votre historique.
            </p>
          </div>
        </div>
      )
    },
    {
      title: "Optimiser votre route",
      icon: <Route className="h-12 w-12 text-cyan-500" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Si vous avez <strong>plusieurs missions le même jour</strong>, utilisez l'optimiseur de route !
          </p>
          <div className="bg-cyan-50 p-4 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Route className="h-5 w-5 text-cyan-600" />
              <p className="font-medium text-cyan-800">Optimiser ma route</p>
            </div>
            <ol className="text-sm text-cyan-700 space-y-2 ml-7 list-decimal">
              <li>Entrez votre adresse de départ</li>
              <li>Cliquez sur "Calculer"</li>
              <li>L'appli vous donne l'ordre optimal</li>
              <li>Ouvrez dans Google Maps ou Waze</li>
            </ol>
          </div>
        </div>
      )
    },
    {
      title: "Signaler un problème",
      icon: <AlertTriangle className="h-12 w-12 text-red-500" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            Si vous rencontrez un problème (dégât, objet cassé, etc.) :
          </p>
          <div className="bg-red-50 p-4 rounded-xl space-y-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <p className="font-medium text-red-800">Cliquez sur "Signaler un problème"</p>
            </div>
            <ol className="text-sm text-red-700 space-y-2 ml-7 list-decimal">
              <li>Décrivez le problème</li>
              <li>Prenez des photos</li>
              <li>L'admin sera notifié immédiatement</li>
            </ol>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Phone className="h-5 w-5" />
            <span>En cas d'urgence, appelez l'agence directement</span>
          </div>
        </div>
      )
    },
    {
      title: "C'est parti !",
      icon: <CheckCircle2 className="h-12 w-12 text-emerald-500" />,
      content: (
        <div className="space-y-4 text-center">
          <p className="text-lg text-gray-700">
            Vous êtes prêt(e) à utiliser l'application !
          </p>
          <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white p-6 rounded-xl">
            <p className="font-bold text-xl mb-2">Bon courage !</p>
            <p className="text-blue-100">
              N'hésitez pas à reconsulter ce guide en cliquant sur le bouton d'aide
            </p>
          </div>
          <p className="text-sm text-gray-500">
            Vous pouvez retrouver ce guide à tout moment via le bouton <HelpCircle className="h-4 w-4 inline" /> en haut de l'écran
          </p>
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowGuide(false);
      setShowBanner(false);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <>
      {/* Floating help button - Bottom right above BottomNav */}
      <button
        onClick={openGuide}
        className="fixed bottom-24 right-4 w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white rounded-full shadow-lg shadow-blue-600/40 flex items-center justify-center z-40 transition-all duration-300 hover:scale-110"
        data-testid="help-button"
        title="Besoin d'aide ?"
      >
        <HelpCircle className="h-5 w-5" />
      </button>

      {/* Guide Dialog */}
      <Dialog open={showGuide} onOpenChange={setShowGuide}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0 flex-shrink-0">
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                {steps[currentStep].icon}
              </div>
              <span>{steps[currentStep].title}</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto p-4">
            {steps[currentStep].content}
          </div>

          {/* Progress and navigation */}
          <div className="p-4 border-t bg-gray-50 flex-shrink-0">
            {/* Progress dots */}
            <div className="flex justify-center gap-1.5 mb-4">
              {steps.map((step, idx) => (
                <button
                  key={step.title || `step-${idx}`}
                  onClick={() => setCurrentStep(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep 
                      ? 'bg-blue-600 w-6' 
                      : idx < currentStep 
                        ? 'bg-blue-300' 
                        : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="flex justify-between gap-3">
              <Button
                variant="outline"
                onClick={prevStep}
                disabled={currentStep === 0}
                className="flex-1"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Précédent
              </Button>
              <Button
                onClick={nextStep}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {currentStep === steps.length - 1 ? (
                  <>
                    Commencer
                    <CheckCircle2 className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Suivant
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HelpGuide;
